// src/components/ProjectDeliverablesTable.js
import React, { useMemo, useState } from "react";
import Papa from "papaparse";

const DEFAULT_VISIBLE = [
    "Rank",
    "Customer",
    "Go-live",
    "Item",
    "Type",
    "ADO",
    "Team",
    "SCRUM",
    "Discovery Target",
    "Reqs Target",
    "Dev Target",
    "UAT Target",
    "Prod Target",
    "Prod Accepted",
    "C/T Target",
    "Total Hrs",
    "In End Game",
];

const sortCycle = (dir) => (dir === "asc" ? "desc" : dir === "desc" ? null : "asc");

const asType = (v) => {
    if (v === null || v === undefined) return { t: "empty", v: "" };
    const s = String(v).trim();
    if (s === "") return { t: "empty", v: "" };
    const n = Number(s.replace(/[, ]/g, ""));
    if (!Number.isNaN(n) && s.match(/^-?\d+(\.\d+)?(e[+-]?\d+)?$/i)) return { t: "num", v: n };
    const d = Date.parse(s);
    if (!Number.isNaN(d)) return { t: "date", v: d };
    return { t: "str", v: s.toLowerCase() };
};

const cmp = (a, b) => {
    const A = asType(a), B = asType(b);
    if (A.t === "empty" && B.t !== "empty") return 1;
    if (B.t === "empty" && A.t !== "empty") return -1;
    if (A.t === B.t) return A.v < B.v ? -1 : A.v > B.v ? 1 : 0;
    return String(a).localeCompare(String(b));
};

// parse a single filter string into a condition (supports !term / NOT term)
const parseFilter = (raw) => {
    const s = String(raw ?? "").trim();
    if (!s) return null;
    if (/^(!|NOT\s+)/i.test(s)) {
        const needle = s.replace(/^(!|NOT\s+)/i, "").toLowerCase();
        return { mode: "not", needle };
    }
    return { mode: "contains", needle: s.toLowerCase() };
};

const ProjectDeliverablesTable = ({
                                      defaultVisibleColumns = DEFAULT_VISIBLE,
                                      backEndUrl
                                  }) => {
    const [rows, setRows] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [visibleCols, setVisibleCols] = useState(defaultVisibleColumns);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [sortBy, setSortBy] = useState(null);
    const [filters, setFilters] = useState({});

    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState("add");
    const [editIndex, setEditIndex] = useState(null);
    const [formData, setFormData] = useState({});

    // save-to-cloud UI state
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState(null);

    const parseCsv = (text) => {
        setLoading(true);
        setError(null);
        Papa.parse(text, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (h) => h.replace(/\s+/g, " ").trim(),
            complete: (res) => {
                const data = res.data || [];
                const cols = res.meta?.fields || [];
                setRows(data);
                setHeaders(cols);
                setVisibleCols((prev) => {
                    const initial = prev.length ? prev : cols.slice(0, 6);
                    return initial.filter((c) => cols.includes(c));
                });
                setFilters({});
                setFormOpen(false);
                setSortBy(cols.includes("Rank") ? { col: "Rank", dir: "asc" } : null);
                setLoading(false);
            },
            error: (err) => {
                setError(`Failed to parse CSV: ${err.message}`);
                setLoading(false);
            },
        });
    };

    const onFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => parseCsv(String(reader.result || ""));
        reader.onerror = () => setError("Could not read the file.");
        reader.readAsText(f);
    };

    const toggleCol = (c) =>
        setVisibleCols((prev) =>
            prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
        );

    const orderedVisible = headers.filter((h) => visibleCols.includes(h));

    const filteredSortedRows = useMemo(() => {
        let data = rows.map((r, idx) => ({ __i: idx, ...r }));

        const conditions = Object.entries(filters)
            .map(([col, raw]) => ({ col, cond: parseFilter(raw) }))
            .filter(({ cond }) => !!cond);

        if (conditions.length) {
            data = data.filter((r) =>
                conditions.every(({ col, cond }) => {
                    const hay = r[col] == null ? "" : String(r[col]).toLowerCase();
                    return cond.mode === "not"
                        ? !hay.includes(cond.needle)
                        : hay.includes(cond.needle);
                })
            );
        }

        if (sortBy?.col && sortBy?.dir) {
            const col = sortBy.col;
            const dir = sortBy.dir === "asc" ? 1 : -1;
            data = [...data].sort((a, b) => dir * cmp(a[col], b[col]));
        }

        return data;
    }, [rows, filters, sortBy]);

    const onHeaderClick = (col) => {
        setSortBy((prev) => {
            if (!prev || prev.col !== col) return { col, dir: "asc" };
            const nextDir = sortCycle(prev.dir);
            return nextDir ? { col, dir: nextDir } : null;
        });
    };

    const sortIndicator = (col) =>
        sortBy?.col === col ? (sortBy.dir === "asc" ? " ▲" : " ▼") : "";

    const clearFilters = () => setFilters({});
    const resetSort = () => {
        if (headers.includes("Rank")) setSortBy({ col: "Rank", dir: "asc" });
        else setSortBy(null);
    };

    // --- Form helpers ---
    const openAddForm = () => {
        const empty = headers.reduce((acc, h) => ((acc[h] = ""), acc), {});
        setFormData(empty);
        setFormMode("add");
        setEditIndex(null);
        setFormOpen((o) => !o);
    };

    const openEditForm = (rowIdx) => {
        const row = rows[rowIdx] || {};
        const filled = headers.reduce((acc, h) => ((acc[h] = row[h] ?? ""), acc), {});
        setFormData(filled);
        setFormMode("edit");
        setEditIndex(rowIdx);
        setFormOpen(true);
    };

    const submitForm = (e) => {
        e.preventDefault();
        if (formMode === "add") {
            setRows((prev) => [...prev, formData]);
        } else if (formMode === "edit" && editIndex != null) {
            setRows((prev) => prev.map((r, i) => (i === editIndex ? formData : r)));
        }
        setFormOpen(false);
    };

    // --- CSV Downloads ---
    const downloadCsv = (dataRows, fileLabel) => {
        if (!headers.length) return;
        const csv = Papa.unparse({
            fields: headers,
            data: dataRows.map((r) => headers.map((h) => r[h] ?? "")),
        });
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `project-deliverables-${fileLabel}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // --- Save to Cloud ---
    const saveToCloud = async () => {
        if (!headers.length) return;
        if (!backEndUrl) {
            setSaveMsg("No backEndUrl provided.");
            return;
        }
        setSaving(true);
        setSaveMsg(null);
        try {
            const payloadRows = rows.map((r) => {
                const o = {};
                headers.forEach((h) => { o[h] = r[h] ?? ""; });
                return o;
            });

            const token = window.localStorage.getItem("pd_token");

            const res = await fetch(backEndUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    headers,
                    rows: payloadRows,
                    meta: {
                        filteredCount: payloadRows.length,
                        totalCount: rows.length,
                        exportedAt: new Date().toISOString(),
                    },
                }),
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(`Save failed (${res.status}): ${text || res.statusText}`);
            }

            setSaveMsg(`Saved ${payloadRows.length} record(s) to cloud successfully.`);
        } catch (e) {
            setSaveMsg(e.message || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    // --- Render helpers ---
    const renderCell = (col, value) => {
        if (col === "ADO") {
            const id = String(value ?? "").trim();
            if (!id || id.toLowerCase() === "n/a") return "";
            const href = `https://dev.azure.com/campusmgmt-ps/INTS/_workitems/edit/${encodeURIComponent(id)}`;
            return (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Open ADO work item ${id}`}
                >
                    {id}
                </a>
            );
        }
        return value ?? "";
    };

    return (
        <div className="bb-container">
            <h1 className="bb-title">Project Deliverables</h1>

            <div className="bb-controls" style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8 }}>
                    Upload CSV:
                    <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ marginLeft: 8 }} />
                </label>

                {headers.length > 0 && (
                    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <a href="#add-update" onClick={(e) => { e.preventDefault(); openAddForm(); }}>
                            {formOpen ? "Close Form" : "Add / Update Row"}
                        </a>
                        <button className="bb-button" onClick={() => downloadCsv(rows, "all")}>
                            Download CSV (All)
                        </button>
                        <button className="bb-button" onClick={() => downloadCsv(filteredSortedRows, "filtered")}>
                            Download CSV (Filtered)
                        </button>
                        <button className="bb-button" onClick={saveToCloud}>
                           Save to Cloud
                        </button>
                        <button className="bb-button" onClick={resetSort}>Reset Sort</button>
                        <button className="bb-button" onClick={clearFilters}>Clear Filters</button>
                    </div>
                )}
                {saveMsg && (
                    <div style={{ marginTop: 8, color: saveMsg.startsWith("Saved") ? "green" : "crimson" }}>
                        {saveMsg}
                    </div>
                )}
            </div>

            {loading && <div>Parsing CSV…</div>}
            {error && (
                <div className="bb-error" style={{ marginBottom: 12 }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {headers.length > 0 && (
                <>
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Select columns to display:</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                            {headers.map((h) => (
                                <label key={h} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                                    <input
                                        type="checkbox"
                                        checked={visibleCols.includes(h)}
                                        onChange={() => toggleCol(h)}
                                    />
                                    <span>{h}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {formOpen && (
                        <div className="bb-card" style={{ marginBottom: 16, padding: 16 }}>
                            <h2 className="bb-title" style={{ fontSize: 18, marginBottom: 12 }}>
                                {formMode === "add" ? "Add Row" : "Update Row"}
                            </h2>
                            <form onSubmit={submitForm}>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                                        gap: 12,
                                        marginBottom: 12,
                                    }}
                                >
                                    {headers.map((h) => (
                                        <label key={h} style={{ display: "grid", gap: 4 }}>
                                            <span style={{ fontSize: 12, color: "#555" }}>{h}</span>
                                            <input
                                                type="text"
                                                value={formData[h] ?? ""}
                                                onChange={(e) => setFormData((fd) => ({ ...fd, [h]: e.target.value }))}
                                            />
                                        </label>
                                    ))}
                                </div>
                                <div style={{ display: "flex", gap: 8 }}>
                                    <button className="bb-button" type="submit">
                                        {formMode === "add" ? "Add" : "Update"}
                                    </button>
                                    <button
                                        className="bb-button"
                                        type="button"
                                        onClick={() => setFormOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </>
            )}

            {orderedVisible.length > 0 && (
                <table className="bb-table">
                    <thead>
                    <tr>
                        <th>Actions</th>
                        {orderedVisible.map((h) => (
                            <th
                                key={h}
                                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                                onClick={() => onHeaderClick(h)}
                                title="Click to sort"
                            >
                                {h}{sortIndicator(h)}
                            </th>
                        ))}
                    </tr>
                    <tr>
                        <th />
                        {orderedVisible.map((h) => (
                            <th key={h}>
                                <input
                                    type="text"
                                    value={filters[h] ?? ""}
                                    onChange={(e) => setFilters((f) => ({ ...f, [h]: e.target.value }))}
                                    placeholder="filter… (use !term or NOT term)"
                                    style={{ width: "100%" }}
                                />
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {filteredSortedRows.length === 0 ? (
                        <tr>
                            <td colSpan={orderedVisible.length + 1} style={{ textAlign: "center", color: "#666" }}>
                                No rows match your filters.
                            </td>
                        </tr>
                    ) : (
                        filteredSortedRows.map((r) => (
                            <tr key={r.__i}>
                                <td>
                                    <a href="#edit" onClick={(e) => { e.preventDefault(); openEditForm(r.__i); }}>
                                        Edit
                                    </a>
                                </td>
                                {orderedVisible.map((h) => (
                                    <td key={h}>{renderCell(h, r[h])}</td>
                                ))}
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            )}

            {rows.length === 0 && !loading && headers.length === 0 && <div>Upload a CSV to begin.</div>}
        </div>
    );
};

export default ProjectDeliverablesTable;
